import {
  FixedResultPagination,
  PaginatedResponse,
  StateId,
  utils,
} from '@wallaby-ui/core';
import {isEmpty, isEqual, isNil} from 'lodash';
import * as React from 'react';
import {useMemo} from 'react';
import {connect, ConnectedProps} from 'react-redux';

import {AgGrid, AgGridOwnProps} from './AgGrid';
import styles from './AgGridWrapper.less';
import {getTableColumnsDataGridProjection} from '../grid_projection';
import {
  getCurrentFilterButton,
  getGridFilters,
  getGridScroll,
  getIsPerformingAction,
} from '../grid_selectors';
import {gridSlice} from '../grid_slice';
import {
  AgGridPagination,
  MemorizedColumnStates,
  SelectedRow,
} from '../grid_types';
import {LIBRARY_NAME} from 'src/js/common/constants';
import Filter from 'src/js/lib/filtering/filters/filter';
import {TableConfig} from 'src/js/model/table_config';
import {RootState} from 'src/js/store';

export interface OwnProps {
  gridConfig?: TableConfig;
  gridId: string;
  gridStateId: StateId;
  onLoadingChange?: (isLoading: boolean) => void;
  handlePageChange?: (page: number) => void;
  handlePageSizeChange?: (size: number) => void;
  agGridPagination?: AgGridPagination;
  rowsData: PaginatedResponse['content'] | undefined;
  disablePagination?: boolean;
  autoPageSize?: boolean;
  selectedRow?: SelectedRow;
  showPageSize?: boolean;
  handleColumnStatesChange?: (columnStates: MemorizedColumnStates) => void;
}

const mapState = (state: RootState, props: OwnProps) => ({
  isPerformingAction: getIsPerformingAction(state, props),
  gridScroll: getGridScroll(state, props),
  gridFilters: getGridFilters(state, props),
  currentFilterButtonId: getCurrentFilterButton(state, props),
});

const mapDispatch = {
  changeGridParams: gridSlice.actions.changeGridParams,
  updateGridPerformedAction: gridSlice.actions.updatePerformedAction,
};

const connector = connect(mapState, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = OwnProps &
  PropsFromRedux &
  Omit<
    AgGridOwnProps,
    | 'columns'
    | 'currentFilter'
    | 'currentScroll'
    | 'gridOptions'
    | 'rows'
    | 'id'
    | 'isPerformingAction'
    | 'onScrollChange'
    | 'onFilterChange'
    | 'onUpdatePerformedAction'
    | 'stateId'
    | 'onColumnStatesChange'
  >;

const logger = utils.LogUtils.getLogger('AgGridWrapper', LIBRARY_NAME);

const AgGridWrapper: React.FC<Props> = (props) => {
  const {
    rowsData,
    gridConfig,
    changeGridParams,
    updateGridPerformedAction,
    gridStateId,
    gridId,
    isPerformingAction,
    gridScroll,
    gridFilters,
    disablePagination = false,
    handlePageChange,
    handlePageSizeChange,
    currentFilterButtonId,
    agGridPagination,
    autoPageSize = false,
    showPageSize,
    handleColumnStatesChange,
    ...restProps
  } = props;

  const handleFilterChange = (filterModel) => {
    if (filterModel) {
      if (!Filter.areFilterModelsEqual(filterModel, gridFilters)) {
        if (gridId) {
          // change result filtering
          const params = {
            ...(!agGridPagination || agGridPagination.page !== 1
              ? {page: 1}
              : {}),
            filters: filterModel,
          };

          // change component state
          changeGridParams(gridStateId, {params});

          logger.warn(
            'You are using column filtering on backend served paged data. ' +
              'The column filter will be applied only on the current page.'
          );
        }
      }
    }
  };

  const handleScrollChange = (scrollPosition) => {
    if (scrollPosition) {
      if (!isEqual(scrollPosition, gridScroll)) {
        if (gridId) {
          // change result scrolling
          const params = {scroll: scrollPosition};

          // change component state
          changeGridParams(gridStateId, {params});
        }
      }
    }
  };

  const handleFilterButtonChange = (filterButtonId: number) => {
    if (filterButtonId || filterButtonId === 0) {
      const params = {filterButtonId};

      changeGridParams(gridStateId, {params});
    }
  };

  const handleUpdateGridPerformedAction = (
    componentId,
    context,
    performedActionType,
    remove = true,
    reset = false
  ) => {
    updateGridPerformedAction(gridStateId, {
      performedAction: performedActionType,
      remove,
      reset,
    });
  };

  const tableColumnsDataGridProjection = useMemo(
    () => getTableColumnsDataGridProjection(gridConfig?.getColumns()),
    [gridConfig]
  );

  // AgGridPagination has to be loaded before first render (if it is supplied)
  return gridConfig && (disablePagination || agGridPagination) ? (
    <div className={styles.dlTableWrapper}>
      <div
        data-name={'ag-grid-wrapper--ag-grid-with-pagination'}
        className={'dlFlexBoxContainerInContainer'}
      >
        <AgGrid
          {...restProps}
          columns={tableColumnsDataGridProjection}
          currentFilter={gridFilters}
          currentScroll={gridScroll}
          filterButtonsConfig={gridConfig?.getValidFilterButtons()}
          currentFilterButton={currentFilterButtonId}
          stateId={gridStateId}
          gridOptions={gridConfig?.getDataGridOptions()}
          rows={rowsData}
          id={gridId}
          isPerformingAction={isPerformingAction}
          onScrollChange={handleScrollChange}
          onFilterChange={handleFilterChange}
          onUpdatePerformedAction={handleUpdateGridPerformedAction}
          onFilterButtonClick={handleFilterButtonChange}
          autoPageSize={autoPageSize}
          selectedRowIdDataPath={gridConfig?.getSelectedRowIdDataPath()}
          onColumnStatesChange={handleColumnStatesChange}
        />
        {!isEmpty(agGridPagination) &&
        !isNil(agGridPagination.pages) &&
        !disablePagination ? (
          <FixedResultPagination
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
            activePage={agGridPagination.page}
            activeSize={agGridPagination.activeSize}
            pages={agGridPagination.pages}
            pageSize={agGridPagination.pageSize}
            countInPage={agGridPagination.countInPage}
            resultCount={agGridPagination.resultCount}
            showResultCount
            showPageSize={showPageSize}
          />
        ) : null}
      </div>
    </div>
  ) : null;
};

export default connector(AgGridWrapper);
